import {
  h1,
  h2,
  h3,
  p,
  strong,
  em,
  ul,
  code,
  pre,
  hr,
  blockquote,
  wrapHTML,
  parseMarkdownFull,
} from './htmlUtils';

/**
 * Format Marketing Strategist data as HTML (matches MarketingStrategistRenderer.tsx)
 * Handles: Website Structure, Positioning Doc, Blog Post, Landing Page
 */
export const formatMarketingStrategistHTML = (data: any): string => {
  if (!data || (!data.hero && !data.sections && !data.site_architecture)) {
    return wrapHTML(p('No marketing strategist content available'));
  }

  const pageType = data.page_type || 'general';
  const isBlogPost = pageType === 'blog_post';
  const isPositioningDoc = pageType === 'positioning_document';
  const isWebsiteStructure = pageType === 'website_structure';
  const isLandingPage = pageType === 'landing_page';

  // Positioning Document Formatting
  if (isPositioningDoc) {
    return formatPositioningDoc(data);
  }

  // Website Structure Formatting
  if (isWebsiteStructure && data.site_architecture) {
    return formatWebsiteStructure(data);
  }

  // Blog Post Formatting
  if (isBlogPost) {
    return formatBlogPost(data);
  }

  // Landing Page Formatting
  if (isLandingPage) {
    return formatLandingPageFull(data);
  }

  // Default/Fallback Landing Page Formatting
  return formatLandingPageDefault(data);
};

/**
 * Format Positioning Document
 */
function formatPositioningDoc(data: any): string {
  const sections: string[] = [];

  // Page Type Badge
  sections.push(p(strong('📋 Positioning Document')));

  // Title
  if (data.hero?.h1) {
    sections.push(h1(data.hero.h1));
  }

  // Strategic Content with Markdown to HTML conversion
  if (data.sections && data.sections.length > 0) {
    data.sections.forEach((section: any) => {
      if (section.heading) {
        sections.push(h2(section.heading));
      }

      if (section.subsections && section.subsections.length > 0) {
        section.subsections.forEach((subsection: any) => {
          if (subsection.subheading) {
            sections.push(h3(subsection.subheading));
          }
          if (subsection.content) {
            // Convert markdown to HTML using existing utility
            sections.push(parseMarkdownFull(subsection.content));
          }
        });
      }
    });
  }

  return wrapHTML(sections.join(''));
}

/**
 * Format Website Structure
 */
function formatWebsiteStructure(data: any): string {
  const sections: string[] = [];

  // Page Type Badge
  sections.push(p(strong('🏗️ Website Structure')));

  // Title
  if (data.hero?.h1) {
    sections.push(h1(data.hero.h1));
  }

  // Phase 1: Positioning Inputs
  if (data.site_architecture.positioning_inputs) {
    sections.push(h2('Phase 1: Positioning Inputs'));
    const pi = data.site_architecture.positioning_inputs;
    sections.push(p(`${strong('Primary ICP:')} ${pi.primary_icp || 'N/A'}`));
    if (pi.secondary_icps?.length > 0) {
      sections.push(p(`${strong('Secondary ICPs:')} ${pi.secondary_icps.join(', ')}`));
    }
    sections.push(p(`${strong('Job to be Done:')} ${pi.job_to_be_done || 'N/A'}`));
    sections.push(p(`${strong('Core Pain:')} ${pi.core_pain || 'N/A'}`));
    sections.push(p(`${strong('Category to Own:')} ${pi.category_to_own || 'N/A'}`));
    if (pi.alternatives?.length > 0) {
      sections.push(p(`${strong('Alternatives:')} ${pi.alternatives.join(', ')}`));
    }
    sections.push(p(`${strong('Value Proposition:')} ${pi.value_proposition || 'N/A'}`));
    sections.push(p(`${strong('Disqualifier:')} ${pi.disqualifier || 'N/A'}`));
  }

  // Phase 2: Keyword Clusters
  if (data.site_architecture.keyword_clusters?.length > 0) {
    sections.push(h2('Phase 2: Keyword Clusters'));
    let tableHtml =
      '<table><thead><tr><th>Cluster</th><th>Intent</th><th>Funnel</th><th>ICP</th><th>Page</th></tr></thead><tbody>';
    data.site_architecture.keyword_clusters.forEach((cluster: any) => {
      tableHtml += `<tr><td>${cluster.cluster_name}</td><td>${cluster.intent_type}</td><td>${cluster.funnel_stage?.replace('_', ' ')}</td><td>${cluster.icp_relevance}</td><td>${code(cluster.assigned_page)}</td></tr>`;
    });
    tableHtml += '</tbody></table>';
    sections.push(tableHtml);
  }

  // Phase 3-4: Page Types & URL Hierarchy
  if (data.site_architecture.pages && data.site_architecture.pages.length > 0) {
    sections.push(h2('Phase 3-4: Page Types & URL Hierarchy'));

    data.site_architecture.pages.forEach((page: any) => {
      sections.push(h3(page.page_name));
      sections.push(p(code(page.url_slug)));
      const metadata = [];
      if (page.page_type) metadata.push(`Type: ${page.page_type}`);
      if (page.parent_page) metadata.push(`Parent: ${code(page.parent_page)}`);
      if (metadata.length > 0) sections.push(p(metadata.join(' | ')));
      sections.push(p(em(page.page_purpose)));

      {
        // Backwards compatible: support both old (nested content_hierarchy) and new (flat) formats
        const pageH1 = page.h1 || page.content_hierarchy?.h1;
        const isOldFormat = page.content_hierarchy && !page.h1;
        const h2Sections = isOldFormat ? page.content_hierarchy.h2_sections : page.h2_sections;

        if (pageH1 || h2Sections) {
          sections.push(p(strong('Content Hierarchy')));
          if (pageH1) sections.push(p(`${strong('H1:')} ${pageH1}`));

          if (h2Sections && h2Sections.length > 0) {
            h2Sections.forEach((h2Item: any) => {
              const isObject = typeof h2Item === 'object';
              sections.push(p(`${strong('H2:')} ${isObject ? h2Item.h2 : h2Item}`));

              if (isObject && h2Item.h3_subsections && h2Item.h3_subsections.length > 0) {
                h2Item.h3_subsections.forEach((h3val: string) => {
                  sections.push(p(`${strong('H3:')} ${h3val}`));
                });
              }
              if (isObject && h2Item.key_messaging) {
                sections.push(p(`${strong('Messaging:')} ${h2Item.key_messaging}`));
              }
              if (isObject && h2Item.cta) {
                sections.push(p(`${strong('CTA:')} ${h2Item.cta}`));
              }
            });
          }

          if (page.primary_cta) {
            sections.push(p(`${strong('CTA:')} ${page.primary_cta}`));
          }
        }
      }

      sections.push(hr());
    });
  }

  // Phase 5: Navigation Structure
  if (data.site_architecture.navigation_structure) {
    sections.push(h2('Phase 5: Navigation Structure'));

    if (data.site_architecture.navigation_structure.primary_nav) {
      sections.push(p(strong('Primary Navigation:')));
      sections.push(p(data.site_architecture.navigation_structure.primary_nav.join(', ')));
    }

    if (data.site_architecture.navigation_structure.secondary_nav) {
      sections.push(p(strong('Secondary Navigation:')));
      sections.push(p(data.site_architecture.navigation_structure.secondary_nav.join(', ')));
    }

    if (data.site_architecture.navigation_structure.footer_nav) {
      sections.push(p(strong('Footer Navigation:')));
      sections.push(p(data.site_architecture.navigation_structure.footer_nav.join(', ')));
    }
  }

  // Phase 6: Internal Linking Rules
  if (
    data.site_architecture.internal_linking_rules &&
    Object.keys(data.site_architecture.internal_linking_rules).length > 0
  ) {
    sections.push(h2('Phase 6: Internal Linking Rules'));
    Object.entries(data.site_architecture.internal_linking_rules).forEach(
      ([pageType, rules]: [string, any]) => {
        sections.push(p(strong(pageType.replace('_', ' ') + ':')));
        sections.push(ul(rules));
      },
    );
  }

  // Sitemap
  if (data.site_architecture.sitemap) {
    sections.push(h2('Sitemap'));
    sections.push(pre(data.site_architecture.sitemap));
  }

  return wrapHTML(sections.join(''));
}

/**
 * Format Blog Post
 */
function formatBlogPost(data: any): string {
  const sections: string[] = [];

  // Page Type Badge
  sections.push(p(strong('📝 Blog Post')));

  // SEO Metadata
  if (data.meta_title?.trim() || data.meta_description?.trim()) {
    sections.push(h2('SEO Metadata'));
    if (data.meta_title?.trim()) {
      sections.push(
        p(`${strong('Meta Title:')} ${data.meta_title} (${Math.min(data.meta_title?.length || 0, 60)}/60)`),
      );
    }
    if (data.meta_description?.trim()) {
      sections.push(
        p(
          `${strong('Meta Description:')} ${data.meta_description} (${Math.min(data.meta_description?.length || 0, 160)}/160)`,
        ),
      );
    }
  }

  // Article Title
  if (data.hero?.h1) {
    sections.push(h1(data.hero.h1));
  }

  // Article Content
  if (data.sections && data.sections.length > 0) {
    data.sections.forEach((section: any) => {
      if (section.heading) {
        sections.push(h2(section.heading));
      }

      if (section.subsections && section.subsections.length > 0) {
        section.subsections.forEach((subsection: any) => {
          if (subsection.subheading) {
            sections.push(h3(subsection.subheading));
          }
          if (subsection.content) {
            sections.push(p(subsection.content.replace(/\n/g, '<br/>')));
          }
        });
      }
    });
  }

  // FAQs
  if (data.faqs && data.faqs.length > 0) {
    sections.push(h2('Frequently Asked Questions'));
    data.faqs.forEach((faq: any) => {
      sections.push(h3(faq.question));
      sections.push(p(faq.answer.replace(/\n/g, '<br/>')));
    });
  }

  // Final CTA
  if (data.cta_final) {
    sections.push(h2('Call to Action'));
    sections.push(p(data.cta_final.replace(/\n/g, '<br/>')));
  }

  // SEO Suggestions
  if (
    (data.internal_links && data.internal_links.length > 0) ||
    (data.image_alt_texts && data.image_alt_texts.length > 0)
  ) {
    sections.push(h2('SEO Suggestions'));

    if (data.internal_links && data.internal_links.length > 0) {
      sections.push(h3('Internal Links'));
      sections.push(ul(data.internal_links));
    }

    if (data.image_alt_texts && data.image_alt_texts.length > 0) {
      sections.push(h3('Image Alt Texts'));
      sections.push(ul(data.image_alt_texts));
    }
  }

  return wrapHTML(sections.join(''));
}

/**
 * Format Landing Page (Full)
 */
function formatLandingPageFull(data: any): string {
  const sections: string[] = [];

  // Page Type Badge
  sections.push(p(strong('🚀 Landing Page')));

  // SEO Metadata
  if (data.meta_title?.trim() || data.meta_description?.trim()) {
    sections.push(h2('SEO Metadata'));
    if (data.meta_title?.trim()) {
      sections.push(
        p(`${strong('Meta Title:')} ${data.meta_title} (${Math.min(data.meta_title?.length || 0, 60)}/60)`),
      );
    }
    if (data.meta_description?.trim()) {
      sections.push(
        p(
          `${strong('Meta Description:')} ${data.meta_description} (${Math.min(data.meta_description?.length || 0, 160)}/160)`,
        ),
      );
    }
  }

  // SEO Keywords
  if (data.seo_keywords && data.seo_keywords.length > 0) {
    sections.push(h2('SEO Keywords'));
    sections.push(p(data.seo_keywords.join(', ')));
  }

  // Hero Section
  if (data.hero) {
    sections.push(h2('Hero Section'));
    sections.push(h1(data.hero.h1));

    if (data.hero.h2) {
      sections.push(p(data.hero.h2));
    }
    if (data.hero.visual_placeholder) {
      sections.push(p(em(` Visual: ${data.hero.visual_placeholder}`)));
    }
    if (data.hero.primary_cta) {
      sections.push(p(strong(`Primary CTA: ${data.hero.primary_cta}`)));
    }
    if (data.hero.secondary_cta) {
      sections.push(p(strong(`Secondary CTA: ${data.hero.secondary_cta}`)));
    }
  }

  // CTA Variations
  if (data.cta_variations && data.cta_variations.length > 0) {
    sections.push(h2('CTA Variations (A/B Testing)'));
    data.cta_variations.forEach((cta: string, idx: number) => {
      sections.push(p(`${idx + 1}. ${cta}`));
    });
  }

  // Main Content Sections
  if (data.sections && data.sections.length > 0) {
    data.sections.forEach((section: any) => {
      sections.push(h2(section.heading));

      if (section.subsections && section.subsections.length > 0) {
        section.subsections.forEach((subsection: any) => {
          if (subsection.subheading) {
            sections.push(h3(subsection.subheading));
          }
          if (subsection.content) {
            // Use parseMarkdownFull to support markdown tables and formatting
            sections.push(parseMarkdownFull(subsection.content));
          }
          if (subsection.visual_placeholder) {
            sections.push(p(em(` Visual: ${subsection.visual_placeholder}`)));
          }
          if (subsection.cta) {
            sections.push(p(strong(`CTA: ${subsection.cta}`)));
          }
        });
      }
    });
  }

  // Social Proof
  const hasSocialProofHTML =
    data.social_proof &&
    (data.social_proof.testimonials?.length > 0 ||
      data.social_proof.client_logos?.length > 0 ||
      data.social_proof.stats?.length > 0);

  if (hasSocialProofHTML) {
    sections.push(h2('Social Proof'));

    if (data.social_proof.testimonials && data.social_proof.testimonials.length > 0) {
      sections.push(h3('Testimonials'));
      data.social_proof.testimonials.forEach((testimonial: any) => {
        if (testimonial.quote) {
          sections.push(blockquote(testimonial.quote));
          const attribution = [testimonial.author, testimonial.role].filter(Boolean).join(', ');
          if (attribution) {
            sections.push(p(`— ${attribution}`));
          }
        }
      });
    }

    if (data.social_proof.stats && data.social_proof.stats.length > 0) {
      sections.push(h3('Statistics'));
      data.social_proof.stats.forEach((stat: any) => {
        if (stat.number && stat.label) {
          sections.push(p(`${strong(stat.number)} ${stat.label}`));
        }
      });
    }

    if (data.social_proof.client_logos && data.social_proof.client_logos.length > 0) {
      sections.push(h3('Client Logos'));
      sections.push(p(data.social_proof.client_logos.filter(Boolean).join(', ')));
    }
  }

  // Credibility
  const hasCredibilityHTML =
    data.credibility &&
    (data.credibility.certifications?.length > 0 ||
      data.credibility.press_mentions?.length > 0 ||
      data.credibility.awards?.length > 0);

  if (hasCredibilityHTML) {
    sections.push(h2('Credibility'));

    if (data.credibility.certifications && data.credibility.certifications.length > 0) {
      sections.push(h3('Certifications'));
      sections.push(ul(data.credibility.certifications.filter(Boolean)));
    }

    if (data.credibility.press_mentions && data.credibility.press_mentions.length > 0) {
      sections.push(h3('Press Mentions'));
      sections.push(ul(data.credibility.press_mentions.filter(Boolean)));
    }

    if (data.credibility.awards && data.credibility.awards.length > 0) {
      sections.push(h3('Awards'));
      sections.push(ul(data.credibility.awards.filter(Boolean)));
    }
  }

  // Pricing
  if (data.pricing && data.pricing.length > 0) {
    sections.push(h2('Pricing'));

    data.pricing.forEach((plan: any) => {
      if (plan.plan_name) {
        sections.push(h3(plan.plan_name));
      }
      if (plan.price) {
        sections.push(p(strong(plan.price)));
      }
      if (plan.features && plan.features.length > 0) {
        sections.push(ul(plan.features.filter(Boolean)));
      }
      if (plan.cta) {
        sections.push(p(strong(`CTA: ${plan.cta}`)));
      }
    });
  }

  // Asset Placeholders
  if (data.asset_placeholders && data.asset_placeholders.length > 0) {
    sections.push(h2('Asset Placeholders'));
    data.asset_placeholders.forEach((asset: any) => {
      if (asset.type && asset.section) {
        sections.push(p(` ${strong(asset.type.toUpperCase() + ' - ' + asset.section)}`));
      }
      if (asset.description) {
        sections.push(p(em(asset.description)));
      }
    });
  }

  // FAQs
  if (data.faqs && data.faqs.length > 0) {
    sections.push(h2('Frequently Asked Questions'));
    data.faqs.forEach((faq: any) => {
      if (faq.question) {
        sections.push(h3(faq.question));
      }
      if (faq.answer) {
        sections.push(p(faq.answer.replace(/\n/g, '<br/>')));
      }
    });
  }

  // Final CTA
  if (data.cta_final) {
    sections.push(h2('Final Call to Action'));
    sections.push(p(data.cta_final.replace(/\n/g, '<br/>')));
  }

  // SEO Suggestions
  if (
    (data.internal_links && data.internal_links.length > 0) ||
    (data.image_alt_texts && data.image_alt_texts.length > 0)
  ) {
    sections.push(h2('SEO Suggestions'));

    if (data.internal_links && data.internal_links.length > 0) {
      sections.push(h3('Internal Links'));
      sections.push(ul(data.internal_links.filter(Boolean)));
    }

    if (data.image_alt_texts && data.image_alt_texts.length > 0) {
      sections.push(h3('Image Alt Texts'));
      sections.push(ul(data.image_alt_texts.filter(Boolean)));
    }
  }

  return wrapHTML(sections.join(''));
}

/**
 * Format Landing Page (Default/Fallback)
 */
function formatLandingPageDefault(data: any): string {
  // Use the same formatting as full landing page
  return formatLandingPageFull(data);
}
